import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { deleteFeatureGroup, listFeatureGroups, updateFeatureGroup } from '../../api/business/endpoints';
import type { FeatureGroup } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { usePermissions } from '../../auth/usePermissions';
import { CompanySelector } from '../../components/CompanySelector';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { missingLanguages, textsOf } from '../../components/localisedText';
import { MissingLanguagesChip } from '../../components/MissingLanguagesChip';
import { PageHeader } from '../../components/PageHeader';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useSelectedCompany } from '../../components/useSelectedCompany';
import { sortRows, useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

import { FeatureGroupDialog } from './FeatureGroupDialog';
import { FeatureValuesDialog } from './FeatureValuesDialog';

type GroupSortKey = 'position' | 'name' | 'values' | 'products';

const VALUE_OF: Record<GroupSortKey, (group: FeatureGroup) => string | number | null> = {
  position: (group) => group.position,
  name: (group) => group.name,
  values: (group) => group.values.length,
  products: (group) => group.productCount,
};

type Dialog =
  | { kind: 'group'; group: FeatureGroup | null }
  | { kind: 'values'; groupId: string }
  | { kind: 'delete'; group: FeatureGroup };

/**
 * The company's feature groups — material, origin, allergens — and their values, which describe its products
 * without making variants (R0030 WP-06). The whole list is held, so it sorts in the browser (Conventions/08 §3).
 */
export const FeatureGroupsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const selection = useSelectedCompany('products.read');
  const company = selection.company;
  const [term, setTerm] = useState('');
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const sort = useSort<GroupSortKey>('position');
  const { languages } = useContentLanguages();
  const canManage =
    company !== null && permissions.writable && permissions.can('products.manage', company.id);

  const query = useQuery({
    queryKey: ['feature-groups', company?.id],
    enabled: company !== null,
    queryFn: () => listFeatureGroups(company?.id ?? ''),
  });
  const move = useAppMutation(
    ({ groupId, position }: { groupId: string; position: number }) =>
      updateFeatureGroup(company?.id ?? '', groupId, { position }),
    ['feature-groups', 'product'],
  );
  const remove = useAppMutation(
    (groupId: string) => deleteFeatureGroup(company?.id ?? '', groupId),
    ['feature-groups', 'product', 'products'],
    () => setDialog(null),
  );

  if (!selection.isLoading && company === null) {
    return (
      <>
        <PageHeader title={t('features.title')} />
        <ErrorNotice error={selection.error} onRetry={selection.retry} />
        <Paper variant="outlined">
          <EmptyState
            title={t('products.noCompany')}
            detail={t('products.noCompanyDetail')}
            action={<Button onClick={() => navigate('/companies')}>{t('nav.companies')}</Button>}
          />
        </Paper>
      </>
    );
  }

  const groups = query.data ?? [];
  const needle = term.trim().toLocaleLowerCase('sk');
  const rows = sortRows(
    groups.filter(
      (group) =>
        needle === '' ||
        group.name.toLocaleLowerCase('sk').includes(needle) ||
        group.values.some((value) => value.value.toLocaleLowerCase('sk').includes(needle)),
    ),
    sort.direction,
    VALUE_OF[sort.key],
  );
  const valuesOf =
    dialog?.kind === 'values' ? groups.find((group) => group.id === dialog.groupId) : undefined;

  return (
    <>
      <PageHeader
        title={t('features.title')}
        subtitle={t('features.subtitle')}
        action={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CompanySelector
              companies={selection.companies}
              value={company}
              onChange={(companyId) => selection.select(companyId)}
            />
            {canManage ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialog({ kind: 'group', group: null })}
              >
                {t('features.create')}
              </Button>
            ) : null}
          </Stack>
        }
      />
      <ErrorNotice
        error={query.error ?? selection.error ?? move.error}
        onRetry={() => void query.refetch()}
      />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('common.search')}
            placeholder={t('features.searchHint')}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="position">
                  {t('features.position')}
                </SortableCell>
                <SortableCell sort={sort} column="name">
                  {t('features.name')}
                </SortableCell>
                <SortableCell sort={sort} column="values">
                  {t('features.values')}
                </SortableCell>
                <SortableCell sort={sort} column="products" align="right">
                  {t('features.products')}
                </SortableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading || selection.isLoading ? (
                <TableSkeletonRows columns={5} />
              ) : (
                rows.map((group) => (
                  <TableRow key={group.id} hover>
                    <TableCell sx={{ width: 120, whiteSpace: 'nowrap' }}>
                      {group.position + 1}
                      {canManage ? (
                        <>
                          <Tooltip title={t('common.moveUp')}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={move.isPending || group.position === 0}
                                onClick={() =>
                                  move.mutate({ groupId: group.id, position: group.position - 1 })
                                }
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t('common.moveDown')}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={move.isPending || group.position === groups.length - 1}
                                onClick={() =>
                                  move.mutate({ groupId: group.id, position: group.position + 1 })
                                }
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 560 }}>
                      {group.name}
                      <MissingLanguagesChip
                        missing={missingLanguages(textsOf(group.translations, 'name', languages), languages)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {group.values.length === 0
                          ? t('features.noValues')
                          : group.values.map((value) => (
                              <Chip key={value.id} size="small" variant="outlined" label={value.value} />
                            ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{group.productCount}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button size="small" onClick={() => setDialog({ kind: 'values', groupId: group.id })}>
                          {t('features.editValues')}
                        </Button>
                        {canManage ? (
                          <>
                            <Button size="small" onClick={() => setDialog({ kind: 'group', group })}>
                              {t('features.rename')}
                            </Button>
                            <Button
                              size="small"
                              color="warning"
                              onClick={() => setDialog({ kind: 'delete', group })}
                            >
                              {t('common.delete')}
                            </Button>
                          </>
                        ) : null}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && company !== null && rows.length === 0 ? (
          <EmptyState
            title={t('features.empty')}
            detail={canManage ? t('features.emptyManage') : t('features.emptyRead')}
            action={
              canManage ? (
                <Button startIcon={<AddIcon />} onClick={() => setDialog({ kind: 'group', group: null })}>
                  {t('features.create')}
                </Button>
              ) : undefined
            }
          />
        ) : null}
      </Paper>

      {dialog?.kind === 'group' && company ? (
        <FeatureGroupDialog companyId={company.id} group={dialog.group} onClose={() => setDialog(null)} />
      ) : null}
      {valuesOf && company ? (
        <FeatureValuesDialog
          companyId={company.id}
          group={valuesOf}
          canManage={canManage}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'delete' ? (
        <ConfirmDialog
          title={t('features.deleteTitle', { name: dialog.group.name })}
          message={t('features.deleteMessage', { count: dialog.group.productCount })}
          confirmLabel={t('common.delete')}
          onConfirm={() => remove.mutate(dialog.group.id)}
          onClose={() => setDialog(null)}
          isBusy={remove.isPending}
          error={remove.error}
        />
      ) : null}
    </>
  );
};
